import React, { FC, useCallback, useState } from 'react';

import { AudioConfigTooltipContentfulPaths } from 'Containers/ProfilePage/ProfileAudio';
import { ContentfulMedicareInfo } from 'ContentfulDefaults/types/profileAddtl';
import { ContentfulTextNote } from 'ContentfulWrappers/ContentfulTextNote';
import { useTextContext } from 'Contexts/textContext';
import { Button } from 'DesignLibrary/atoms';
import ToggleList from 'DesignLibrary/atoms/ToggleList';
import { Paragraph } from 'DesignLibrary/atoms/typography';
import { useViewport } from 'DesignLibrary/context';
import Text from 'Shared/Text';
import {
  getMedicareCalloutUrl,
  getMedicareCopyField,
  shouldShowMedicareCallouts,
  trackMedicareProductSelect,
} from 'Utils/medicare';

import { MedicareLink } from './styled';
import { MedicareDestination } from '../../../constants';
import { CUSTOMER_PATH } from '../../../utils/urls';
import Question from '../_shared/Question';
import { QuestionWrapper } from '../_shared/Question/styled';
import { QuestionHorizontalLine } from '../MemberSection/HouseholdMember/styled';
import { Member, UpdateMemberFields } from '../types';

export interface MedicareSectionProps {
  isProfileAudioEnabled: boolean;
  medicareDestination: MedicareDestination;
  hasMedicare: boolean;
  policyholder: Member;
  planYear?: number;
  includeMedicare?: boolean;
  handleEditPolicyholder: (update: UpdateMemberFields) => void;
}

export const MedicareSection: FC<MedicareSectionProps> = ({
  isProfileAudioEnabled,
  medicareDestination,
  hasMedicare,
  policyholder,
  planYear,
  includeMedicare,
  handleEditPolicyholder,
}) => {
  const { retrieveContentfulData } = useTextContext();
  const { device } = useViewport();
  // The link follows the same rule as the section itself: My ALEX Health needs no Medicare product.
  const canShowLearnMore = shouldShowMedicareCallouts(medicareDestination, hasMedicare);
  const [displayLearnMore, setDisplayLearnMore] = useState<boolean>(
    canShowLearnMore && policyholder.on_medicare === false,
  );

  const medicareCard = retrieveContentfulData<ContentfulMedicareInfo>(
    'profile_section_addtl.medicare_info_card',
  );
  const optionY =
    (medicareCard?.profile_callout_button_options && medicareCard?.profile_callout_button_options.yes) ||
    'Yes';
  const optionN =
    (medicareCard?.profile_callout_button_options && medicareCard?.profile_callout_button_options.no) || 'No';

  const medicareUrl = getMedicareCalloutUrl(medicareDestination, CUSTOMER_PATH);

  const handleMedicareChange = useCallback(
    (value: string) => {
      const booleanValue = value === 'yes';
      handleEditPolicyholder({ on_medicare: booleanValue });

      setDisplayLearnMore(canShowLearnMore && value === 'no');
    },
    [handleEditPolicyholder, canShowLearnMore],
  );

  const handleMedicareRedirect = useCallback(() => {
    trackMedicareProductSelect(medicareDestination, medicareUrl);

    const newWindow = window.open(medicareUrl, '_blank');
    if (newWindow) newWindow.focus();
  }, [medicareUrl, medicareDestination]);

  const profileCard = retrieveContentfulData<ContentfulMedicareInfo>(
    'profile_section_addtl.medicare_info_card',
  );
  const renderMedicareTextNotes = () => {
    const shouldShowMedicareNote = hasMedicare || includeMedicare;

    if (!shouldShowMedicareNote) {
      return null;
    }
    const medicareTextNoteForYear = profileCard?.text_notes?.find(
      (n) => (n.plan_year || '').toString() === (planYear || '').toString(),
    );

    if (
      medicareTextNoteForYear &&
      (medicareTextNoteForYear.short_text || medicareTextNoteForYear.long_text)
    ) {
      return <ContentfulTextNote sectionKey="profile_section.medicare" note={medicareTextNoteForYear} />;
    }

    return null;
  };

  return (
    <div id="medicare-section" data-testid="medicare-section">
      <Question
        name="medicareInfo"
        error={false}
        isIncomplete={false}
        audioTooltipsEnabled={isProfileAudioEnabled}
        audioTooltipContentfulPath={AudioConfigTooltipContentfulPaths.MedicareSectionAudioTooltip}
        isMedicareCallout
      >
        <QuestionWrapper className="question-wrapper">
          {medicareCard && (
            <Paragraph data-testid="medicare-question">{medicareCard.profile_callout_question}</Paragraph>
          )}

          <ToggleList
            label={medicareCard.profile_callout_question}
            groupName="medicareQuestion"
            options={[
              {
                option: optionY as string,
                value: 'yes',
              },
              {
                option: optionN as string,
                value: 'no',
              },
            ]}
            handleChange={(e) => handleMedicareChange(e.target.value)}
            selected={
              policyholder.on_medicare === true ? 'yes' : policyholder.on_medicare === false ? 'no' : ''
            }
            color="--colors-gray-black"
            stretch={device === 'mobile'}
          />
        </QuestionWrapper>
        {renderMedicareTextNotes()}
        {displayLearnMore && (
          <>
            <QuestionHorizontalLine />
            <Paragraph>
              {retrieveContentfulData<string>(
                getMedicareCopyField(
                  medicareDestination,
                  retrieveContentfulData,
                  'profile_callout_learn_more',
                ),
              )}
            </Paragraph>
            <MedicareLink>
              <Button
                iconRight="ArrowSquareOut"
                stretch
                size="small"
                onClick={handleMedicareRedirect}
                data-testid="link-to-medicare"
              >
                <Text
                  field={getMedicareCopyField(
                    medicareDestination,
                    retrieveContentfulData,
                    'profile_side_panel_button_text',
                  )}
                />
              </Button>
            </MedicareLink>
          </>
        )}
      </Question>
    </div>
  );
};

export default MedicareSection;
